import { ClearAllPenalties } from "./clear-all-penalties";
import { DeletePenalty } from "./delete-penalty";
import display from "./display";
import { Handler } from "./handler";
import { MainMenu } from "./main-menu";
import { PlayerPenalty } from "./player-penalty";
import { PlayerPenalty4k } from "./player-penalty-4k";
import { Score } from "./score";
import scoreboard from "./scoreboard";
import { SetMainClock } from "./set-main-clock";
import { SetScore } from "./set-score";
import { SelectTimeOut, TeamTimeOut } from "./time-out";
import { Sound, buzzerSound } from "./sound";
import { clockString } from "./util";

export class Clock {
    private mainClock = 0;
    private handler: Handler | null = null;
    private timer: number | undefined;
    private enablePenaltyClocks = true;
    private autoHorn = true;
    private displayTimeAndScoreTimer: number | undefined;
    private beepSound = new Sound("beep.mp3");

    constructor() {}

    displayTime(): void {
        display.line1(
            `TIME  ${clockString(this.mainClock, {
                withTenths: true,
                leftTrim: "&nbsp;",
            })}`
        );
        scoreboard.setMainClockVal(this.mainClock);
    }

    displayScore(): void {
        display.line2(
            `H=${String(scoreboard.getHomeScore()).padStart(
                3,
                " "
            )}  G=${String(scoreboard.getGuestScore()).padStart(3, " ")}  ${
                this.enablePenaltyClocks ? "EN" : "DS"
            }`
        );
    }

    displayTimeAndScore(): void {
        if (this.handler) return;
        if (this.displayTimeAndScoreTimer != null) return;
        this.displayTime();
        this.displayScore();
    }

    displayTimeAndScoreAfter1Second(): void {
        if (this.displayTimeAndScoreTimer)
            clearTimeout(this.displayTimeAndScoreTimer);
        this.displayTimeAndScoreTimer = setTimeout(() => {
            this.displayTimeAndScoreTimer = undefined;
            this.displayTimeAndScore();
        }, 1000);
    }

    getMainClockVal(): number {
        return this.mainClock;
    }

    setMainClockVal(val: number): void {
        if (val < 0) val = 0;

        this.mainClock = val;

        scoreboard.setMainClockVal(this.mainClock);

        // If there's a handler, don't blow away what it might be displaying
        if (this.handler) return;

        this.displayTimeAndScore();
    }

    setHandler(newHandler: Handler): void {
        this.popHandler();

        this.handler = newHandler;
        this.handler.boot();
    }

    popHandler(): void {
        if (this.handler) {
            this.handler.onPop();
            this.handler = null;
        }

        this.displayTimeAndScore();
    }

    stop(): void {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }
        document.getElementById("startLED")!.setAttribute("fill", "black");
    }

    onStop(): void {
        this.beep();
        this.stop();
    }

    onStart(): void {
        this.beep();

        if (this.timer) return;
        if (this.mainClock == 0) return;

        document.getElementById("startLED")!.setAttribute("fill", "lightgreen");

        this.timer = setInterval(() => {
            if (this.mainClock == 0) {
                this.stop();
                if (this.autoHorn) buzzerSound.playFor(3000);
                return;
            }
            this.setMainClockVal(this.mainClock - 100);
            if (this.enablePenaltyClocks) scoreboard.adjustPenalties(-100);
        }, 100);
    }

    onKeypad(n: number): void {
        this.call("onKeypad", n);
    }

    onEnter(): void {
        this.call("onEnter");
    }

    onClear(): void {
        this.call("onClear");
    }

    onUpArrow(): void {
        this.call("onUp");
    }

    onDownArrow(): void {
        this.call("onDown");
    }

    call(f: string, ...args: any[]): void {
        if (this.handler) {
            this.beep();

            (this.handler as any)[f](...args);
        }
    }

    onSetMainClock(): void {
        this.beep();
        if (!this.timer && !this.handler) this.setHandler(new SetMainClock());
    }

    onScoreHome(val: number): void {
        this.beep();
        this.setHandler(new Score("home", val));
    }

    onSetScoreHome(): void {
        this.beep();
        this.setHandler(new SetScore("home"));
    }

    onSetScoreGuest(): void {
        this.beep();
        this.setHandler(new SetScore("guest"));
    }

    onShotsHome(val: number): void {
        this.beep();
        scoreboard.adjustHomeShots(val);
    }

    onScoreGuest(val: number): void {
        this.beep();
        this.setHandler(new Score("guest", val));
    }

    onShotsGuest(val: number): void {
        this.beep();
        scoreboard.adjustGuestShots(val);
    }

    onHomePlayerPenalty(): void {
        this.beep();
        this.setHandler(new PlayerPenalty("home"));
    }

    onHomePlayerPenalty4k(): void {
        this.beep();
        this.setHandler(new PlayerPenalty4k("home"));
    }

    onDeleteHomePenalty(): void {
        this.beep();
        this.setHandler(new DeletePenalty("home"));
    }

    onClearAllHomePenalties(): void {
        this.beep();
        this.setHandler(new ClearAllPenalties("home"));
    }

    onGuestPlayerPenalty(): void {
        this.beep();
        this.setHandler(new PlayerPenalty("guest"));
    }

    onGuestPlayerPenalty4k(): void {
        this.beep();
        this.setHandler(new PlayerPenalty4k("guest"));
    }

    onDeleteGuestPenalty(): void {
        this.beep();
        this.setHandler(new DeletePenalty("guest"));
    }

    onClearAllGuestPenalties(): void {
        this.beep();
        this.setHandler(new ClearAllPenalties("guest"));
    }

    onPeriod(): void {
        this.beep();
        scoreboard.nextPeriod();
    }

    onHornPress(): void {
        buzzerSound.playLooping();
    }

    onHornRelease(): void {
        buzzerSound.stop();
    }

    onAutoHorn(): void {
        this.beep();
        this.autoHorn = !this.autoHorn;
        document.getElementById("autoHornLED")!.setAttribute("fill", this.autoHorn ? "yellow" : "black");
    }

    setEnablePenaltyClocks(val: boolean): void {
        this.beep();
        this.enablePenaltyClocks = val;
        this.displayTimeAndScore();
    }

    onTimeoutHome(): void {
        this.beep();
        if (!this.timer) {
            if (this.handler?.onTimeoutHome) {
                this.handler.onTimeoutHome();
            } else {
                this.setHandler(new TeamTimeOut("home"));
            }
        }
    }

    onTimeoutGuest(): void {
        this.beep();
        if (!this.timer) {
            if (this.handler?.onTimeoutGuest) {
                this.handler.onTimeoutGuest();
            } else {
                this.setHandler(new TeamTimeOut("guest"));
            }
        }
    }

    onTimeoutOnOff(): void {
        this.beep();
        if (!this.timer) {
            if (this.handler?.onTimeoutOnOff) {
                this.handler.onTimeoutOnOff();
            } else {
                this.setHandler(new SelectTimeOut());
            }
        }
    }

    onMenu(): void {
        this.beep();
        if (!this.timer) {
            if (this.handler?.onMenu) {
                this.handler.onMenu();
            } else {
                this.setHandler(new MainMenu());
            }
        }
    }

    private beep(): void {
        this.beepSound.play();
    }
}

export default new Clock();
